import PropTypes from "prop-types";
import React from "react";

import FormButton from "../../../components/form/FormButton";
import FormCheckbox from "../../../components/form/FormCheckbox";
import FormCheckboxDropdown from "../../../components/form/FormCheckboxDropdown";
import FormColorDropdown from "../../../components/form/FormColorDropdown";
import FormDropdown from "../../../components/form/FormDropdown";
import FormIconPicker from "../../../components/form/FormIconPicker";
import FormTextField from "../../../components/form/FormTextField";
import ModalCheckboxGrid from "../../../components/modal/ModalCheckboxGrid";
import ModalDescription from "../../../components/modal/ModalDescription";
import ModalField from "../../../components/modal/ModalField";
import ModalGroup from "../../../components/modal/ModalGroup";
import ModalRow from "../../../components/modal/ModalRow";
import { getCompactDisplayOptions } from "../../../constants/compactDisplay";
import itemTypeCategories, { getitemTypeCategoryOptions } from "../../../constants/itemTypeCategories";
import narrativeFolderIconNames from "../../../constants/narrativeFolderIconNames";
import { REFERENCE_RELATES_TO } from "../../../constants/references";
import { settingsErrorMessages } from "../../../constants/settingsErrors";
import typeColors from "../../../constants/typeColors";
import typeIcons from "../../../constants/typeIcons";
import { typeSelectors } from "../../../modules/definition/types";
import { PropTypeShapes } from "../../../types";
import preprocessorQuery from "../../../utils/preprocessorQuery";
import userInputValidators from "../../../utils/userInputValidators";
import selectors from "../selectors";

const isMobile = preprocessorQuery.isMobile();

const iconOptions = isNarrativeFolder => {
  let options = [];
  const icons = isNarrativeFolder ? narrativeFolderIconNames : typeIcons;
  icons.forEach(i => {
    typeColors.forEach(c => {
      options.push({ color: c, icon: i });
    });
  });
  return options;
};

const renderTopGroup = (t, useProps) => {
  const isIos = preprocessorQuery.isIos();

  const defaultColor = typeSelectors.getDefaultItemColorByType(t);
  const colorOption = useProps.colorOptions.find(c => c.color === defaultColor);
  const defaultColorId = colorOption ? colorOption.value : null;

  return (
    <ModalGroup>
      <ModalRow>
        <ModalField label="Name" flex={1}>
          <FormTextField
            value={t.label}
            onChange={label => useProps.onLabelChange(t.id, label)}
            validator={userInputValidators.noEmpty}
            autoFocusOnEmpty={true}
          />
        </ModalField>
        <ModalField label="Plural" flex={1}>
          <FormTextField
            value={t.pluralLabel}
            onChange={pluralLabel => useProps.onPluralLabelChange(t.id, pluralLabel)}
            validator={userInputValidators.noEmpty}
          />
        </ModalField>
      </ModalRow>
      <ModalRow>
        <ModalField label="Icon" flex={1}>
          <FormIconPicker
            value={{ icon: t.icon, color: t.color }}
            options={iconOptions(t.isNarrativeFolder)}
            id="settingsTypeTabIconPicker"
            onChange={opt => useProps.onIconChange(t.id, opt)}
            title="Icon"
            backText="Settings"
          />
        </ModalField>
        <ModalField label="Default Item Color" flex={1}>
          <FormColorDropdown
            value={defaultColorId}
            options={useProps.colorOptions.toArray()}
            onChange={color => {
              const colorOption = useProps.colorOptions.find(c => c.value === color);
              return useProps.onDefaultItemColorChange(t.id, colorOption.color);
            }}
            title="Color"
            backText="Settings"
          />
        </ModalField>
      </ModalRow>
      <ModalRow>
        <ModalField
          flex={1}
          label="Compact Display"
          helpTooltip="Choose how items are represented when space is limited"
        >
          <FormDropdown
            value={t.compactDisplay}
            options={getCompactDisplayOptions(t.compactDisplay)}
            onChange={val => useProps.onCompactDisplayChange(t.id, val)}
            showDescription={true}
          />
        </ModalField>
        <ModalField label="Identifier Prefix" flex={1}>
          <FormTextField
            value={t.identifierPrefix}
            onChange={prefix => useProps.onPrefixChange(t.id, prefix)}
            disabled={!typeSelectors.isIdentifierType(t)}
            validator={userInputValidators.noEmpty}
          />
        </ModalField>
      </ModalRow>
      <ModalRow>
        <ModalField
          flex={1}
          label="Category"
          helpTooltip="This setting is only used to help group item types within the Settings menus."
        >
          <FormDropdown
            value={t.category}
            options={getitemTypeCategoryOptions(t.category)}
            onChange={val => useProps.onCategoryChange(t.id, val)}
          />
        </ModalField>
        <ModalField showBlankLabel={!isIos}>
          <FormCheckbox
            checkboxState={t.enabled}
            label={"Enabled"}
            onChange={val => useProps.onEnabledChange(t.id, val)}
            disabled={t.isNarrativeFolder}
          />
        </ModalField>
      </ModalRow>
    </ModalGroup>
  );
};

const renderDisplayLabelGroup = (t, useProps) => {
  return (
    <ModalGroup>
      <ModalRow>
        <ModalField
          label="Label Display"
          flex={1}
          helpTooltip="What label should be shown in the inspector for the item's label (e.g. Name for person, Label for event)"
        >
          <FormTextField
            value={t.labelDisplayLabel}
            onChange={label => useProps.onLabelDisplayLabelChange(t.id, label)}
          />
        </ModalField>
      </ModalRow>
      <ModalRow>
        <ModalField
          label="Start Display"
          flex={1}
          helpTooltip="What label should be shown in the inspector for the item's start date (e.g. Birth for person, Start for event)"
        >
          <FormTextField
            disabled={!t.canHaveDate}
            value={t.startDisplayLabel}
            onChange={label => useProps.onStartDisplayLabelChange(t.id, label)}
          />
        </ModalField>
        <ModalField
          label="End Display"
          flex={1}
          helpTooltip="What label should be shown in the inspector for the item's end date (e.g. Death for person, End for event)"
        >
          <FormTextField
            disabled={!t.canHaveDate}
            value={t.endDisplayLabel}
            onChange={label => useProps.onEndDisplayLabelChange(t.id, label)}
          />
        </ModalField>
      </ModalRow>
      <ModalRow>
        <ModalField
          label="Anniversary Display"
          flex={1}
          helpTooltip="What label should be shown in the inspector for an item's anniversary (e.g. Birthday for person, Anniversary for event)"
        >
          <FormTextField
            disabled={!t.canHaveDate}
            value={t.anniversaryDisplayLabel}
            onChange={label => useProps.onAnniversaryDisplayLabelChange(t.id, label)}
          />
        </ModalField>
      </ModalRow>
      <ModalRow>
        <ModalField flex={1}>
          <FormCheckbox
            disabled={!t.canHaveDate}
            checkboxState={t.extendAnniversaryBeyondEnd}
            label="Extend anniversary beyond end date"
            onChange={state => useProps.onExtendAnniversaryBeyondEndChange(t.id, state)}
            helpTooltip="If you choose to show an item of type's anniversary in Timeline view, should it show only until the item's end date, or continue to show beyond the item's end date."
          />
        </ModalField>
      </ModalRow>
    </ModalGroup>
  );
};

const renderShowInGroup = (t, useProps) => {
  const { types } = useProps;
  let showWarning = false;
  types.allIds.map(stId => {
    const st = types.byId.get(stId);
    const isChecked = st.superTypes.indexOf(t.id) >= 0;
    const parentTypeNotAllowed = !t.showInSequence && isChecked && st.showInSequence;
    if (parentTypeNotAllowed) {
      showWarning = true;
    }
  });

  return (
    <ModalGroup>
      <ModalRow>
        <ModalField flex={1} label="Allowed Data">
          <ModalCheckboxGrid columns={1}>
            <FormCheckbox
              key="canHaveDate"
              label="Dates"
              checkboxState={t.canHaveDate}
              onChange={newState => useProps.onCanHaveDateChange(t.id, newState)}
              helpTooltip="Items of this type are allowed to have start dates, end dates, and durations. Items must have dates to be shown as events in Timeline View."
            />
          </ModalCheckboxGrid>
        </ModalField>
      </ModalRow>
      <ModalRow>
        <ModalField flex={1} label="Show In">
          <ModalCheckboxGrid columns={1}>
            <FormCheckbox
              key="sequence"
              label="Chronological order (always)"
              checkboxState={t.showInSequence}
              disabled={t.isNarrativeFolder && !t.showInSequence}
              onChange={newState => useProps.onShowInSequenceChange(t.id, newState)}
              helpTooltip="Items of this type are included in chronological order views (Spreadsheet, Relationship, Subway). Every item of this type will always be included in these views."
              className={showWarning ? "warning" : null}
            />
            <FormCheckbox
              key="narrative"
              label="Narrative order (when explicitly added)"
              checkboxState={t.canIncludeInNarrative}
              onChange={newState => useProps.onIncludeInNarrativeChange(t.id, newState)}
              disabled={t.isNarrativeFolder}
              helpTooltip="Items of this type are allowed in the narrative order views (Narrative, Outline). Items must be explicitly added to the narrative in order to appear in these views."
            />
            <FormCheckbox
              key="timeline"
              label="Timeline View"
              checkboxState={t.canHaveDate && useProps.timelineViewSettings.showTypes.includes(t.id)}
              onChange={newState => useProps.onShowTypeChange(t.id, newState)}
              disabled={!t.canHaveDate}
              helpTooltip="Items of this type that have dates will be shown in Timeline views."
            />
            <FormCheckbox
              key="sidebar"
              label={isMobile ? "Toolbar" : "Sidebar"}
              helpTooltip="This type's icon will be shown in the Sidebar to provide quick access to a list of all items of this type."
              checkboxState={t.showOnSidebar}
              onChange={newState => useProps.onShowOnSidebarChange(t.id, newState)}
            />
            <FormCheckbox
              key="columns"
              label="Relationship comparisons"
              checkboxState={t.showAsColumn}
              onChange={newState => useProps.onShowAsColumnChange(t.id, newState)}
              helpTooltip="Allow items of this type to be chosen as Timeline View groups, Relationship view columns, and Subway view tracks."
            />
          </ModalCheckboxGrid>
        </ModalField>
      </ModalRow>
      {showWarning ? (
        <ModalRow>
          <ModalDescription>
            <div className="warning">{settingsErrorMessages.EVENT_TYPE_PARENT}</div>
          </ModalDescription>
        </ModalRow>
      ) : null}
    </ModalGroup>
  );
};

const renderSuperTypesList = (t, types, onHasSuperTypeChange) => {
  let showWarning = false;
  const checkboxes = types.allIds.map(stId => {
    const st = types.byId.get(stId);
    const isChecked = t.superTypes.indexOf(stId) >= 0;
    const parentTypeNotAllowed = t.showInSequence && isChecked && !st.showInSequence;
    if (parentTypeNotAllowed) {
      showWarning = true;
    }
    return (
      <FormCheckbox
        key={st.id}
        label={st.label}
        checkboxState={isChecked}
        onChange={newState => onHasSuperTypeChange(t.id, st.id, newState)}
        disabled={t.showInSequence ? !(isChecked || st.showInSequence) : false}
        className={parentTypeNotAllowed ? "warning" : null}
      />
    );
  });

  return (
    <ModalGroup key="superTypes">
      <ModalRow>
        <ModalField
          flex={1}
          label="Allowed Parent Types"
          helpTooltip="Items are allowed to be nested as children under the following parent types (e.g. Subtask within a Task, Person within a Team, Location within a Location)"
        >
          <ModalCheckboxGrid columns={2}>{checkboxes}</ModalCheckboxGrid>
        </ModalField>
      </ModalRow>
      {showWarning ? (
        <ModalRow>
          <ModalDescription>
            <div className="warning">{settingsErrorMessages.EVENT_TYPE_PARENT}</div>
          </ModalDescription>
        </ModalRow>
      ) : null}
    </ModalGroup>
  );
};

const renderPropertiesList = (t, properties, onHasPropertyChange) => {
  const options = properties.allIds.map(pId => {
    const p = properties.byId.get(pId);
    const isChecked = t.properties.indexOf(pId) >= 0;
    return {
      label: p.label,
      value: p.id,
      selected: isChecked
    };
  });

  return (
    <ModalGroup key="properties">
      <ModalRow>
        <ModalField
          flex={1}
          label="Properties"
          helpTooltip="Choose which properties are shown for this item type. This setting is mirrored in the Properties tab."
        >
          <FormCheckboxDropdown options={options} onChange={(id, val) => onHasPropertyChange(t.id, id, val)} />
        </ModalField>
      </ModalRow>
    </ModalGroup>
  );
};

const renderReferencesList = (t, references, onHasReferenceChange, types) => {
  const eventTypes = selectors.eventTypesFromAllTypes(types.byId);
  const personTypes = selectors.personTypesFromAllTypes(types.byId);
  const locationTypes = selectors.locationTypesFromAllTypes(types.byId);
  const otherTypes = selectors.otherTypesFromAllTypes(types.byId);

  const toEventReferences = selectors
    .subjectTypeReferences(references.byId, eventTypes)
    .filter(r => r.id !== REFERENCE_RELATES_TO);
  const toPersonReferences = selectors
    .subjectTypeReferences(references.byId, personTypes)
    .filter(r => r.id !== REFERENCE_RELATES_TO && !toEventReferences.find(ref => ref.id === r.id));
  const toLocationReferences = selectors
    .subjectTypeReferences(references.byId, locationTypes)
    .filter(
      r => r.id !== REFERENCE_RELATES_TO && !toEventReferences.merge(toPersonReferences).find(ref => ref.id === r.id)
    );
  const toOtherReferences = selectors.subjectTypeReferences(references.byId, otherTypes).filter(
    r =>
      r.id !== REFERENCE_RELATES_TO &&
      !toEventReferences
        .merge(toPersonReferences)
        .merge(toLocationReferences)
        .find(ref => ref.id === r.id)
  );

  const getOptionList = options =>
    options
      .map(r => {
        const isChecked = t.references.indexOf(r.id) >= 0;
        return {
          label: r.label,
          value: r.id,
          selected: isChecked
        };
      })
      .toList();

  const onChange = (id, val) => onHasReferenceChange(t.id, id, val);

  return (
    <ModalGroup
      key="references"
      label="Relationship Fields"
      helpTooltip="Choose which relationship fields are shown for this item type. This setting is mirrored in the Relationships tab."
    >
      <ModalRow>
        <ModalField flex={1} label="Has relationships to Events called">
          <FormCheckboxDropdown key="eventRefs" options={getOptionList(toEventReferences)} onChange={onChange} />
        </ModalField>
      </ModalRow>
      <ModalRow>
        <ModalField flex={1} label="Has relationships to People called">
          <FormCheckboxDropdown key="peopleRefs" options={getOptionList(toPersonReferences)} onChange={onChange} />
        </ModalField>
      </ModalRow>
      <ModalRow>
        <ModalField flex={1} label="Has relationships to Locations called">
          <FormCheckboxDropdown key="locationRefs" options={getOptionList(toLocationReferences)} onChange={onChange} />
        </ModalField>
      </ModalRow>
      <ModalRow>
        <ModalField flex={1} label="Has relationships to Other Types called">
          <FormCheckboxDropdown key="otherRefs" options={getOptionList(toOtherReferences)} onChange={onChange} />
        </ModalField>
      </ModalRow>
    </ModalGroup>
  );
};

const renderAcademicList = (t, onCanHaveLocationUriChange) => {
  return t.category === itemTypeCategories.LOCATION ? (
    <ModalGroup key="academic">
      <ModalRow>
        <ModalField flex={1} label="Academic">
          <ModalCheckboxGrid columns={1}>
            <FormCheckbox
              key="canHaveLocationUri"
              label="Include Location URI"
              checkboxState={t.canHaveLocationUri}
              onChange={newState => onCanHaveLocationUriChange(t.id, newState)}
              disabled={false}
            />
          </ModalCheckboxGrid>
        </ModalField>
      </ModalRow>
    </ModalGroup>
  ) : null;
};

const SettingsTypeDetailView = props => {
  const isIos = preprocessorQuery.isIos();

  const t = props.selectedItem ? props.types.byId.get(props.selectedItem) : null;
  if (!t) {
    return <div />;
  }

  return (
    <div>
      {renderTopGroup(t, props)}
      {renderDisplayLabelGroup(t, props)}
      {renderShowInGroup(t, props)}
      {renderSuperTypesList(t, props.types, props.onHasSuperTypeChange)}
      {renderPropertiesList(t, props.properties, props.onHasPropertyChange)}
      {renderReferencesList(t, props.references, props.onHasReferenceChange, props.types)}
      {renderAcademicList(t, props.onCanHaveLocationUriChange)}
      {isIos && props.canDelete ? (
        <ModalGroup>
          <ModalRow>
            <div className="settingsDetailDeleteButton">
              <FormButton destructive={true} label="Delete" onClick={props.onDeleteClick} />
            </div>
          </ModalRow>
        </ModalGroup>
      ) : null}
    </div>
  );
};

SettingsTypeDetailView.propTypes = {
  types: PropTypeShapes.denormalisedTypeDefinitionsIdList.isRequired,
  properties: PropTypeShapes.propertyDefinitionsIdList.isRequired,
  references: PropTypeShapes.denormalisedReferenceDefinitionsIdList.isRequired,
  selectedItem: PropTypes.string,
  onLabelChange: PropTypes.func.isRequired,
  onDefaultItemColorChange: PropTypes.func.isRequired,
  onPluralLabelChange: PropTypes.func.isRequired,
  onLabelDisplayLabelChange: PropTypes.func.isRequired,
  onStartDisplayLabelChange: PropTypes.func.isRequired,
  onEndDisplayLabelChange: PropTypes.func.isRequired,
  onAnniversaryDisplayLabelChange: PropTypes.func.isRequired,
  onExtendAnniversaryBeyondEndChange: PropTypes.func.isRequired,
  onIconChange: PropTypes.func.isRequired,
  onCanHaveDateChange: PropTypes.func.isRequired,
  onIncludeInNarrativeChange: PropTypes.func.isRequired,
  onShowInSequenceChange: PropTypes.func.isRequired,
  onShowOnSidebarChange: PropTypes.func.isRequired,
  onShowAsColumnChange: PropTypes.func.isRequired,
  onCanHaveLocationUriChange: PropTypes.func.isRequired,
  onCategoryChange: PropTypes.func.isRequired,
  onEnabledChange: PropTypes.func.isRequired,
  onPrefixChange: PropTypes.func.isRequired,
  onCompactDisplayChange: PropTypes.func.isRequired,
  onHasPropertyChange: PropTypes.func.isRequired,
  onHasReferenceChange: PropTypes.func.isRequired,
  onHasSuperTypeChange: PropTypes.func.isRequired,
  canDelete: PropTypes.bool,
  onDeleteClick: PropTypes.func,
  colorOptions: PropTypeShapes.listOptionList.isRequired,
  timelineViewSettings: PropTypeShapes.timelineViewSettings.isRequired,
  onShowTypeChange: PropTypes.func.isRequired
};

export default SettingsTypeDetailView;
